/** @jsxImportSource @emotion/react */
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  css,
  Box,
  Typography,
  TableFooter,
  IconButton,
  Button,
} from '@mui/material';
import React, { ForwardedRef, forwardRef, useCallback, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { TableVirtuoso } from 'react-virtuoso';
import { useNavigate } from 'react-router-dom';
import {
  commisionPaymentStatusFilterAtom,
  commissionsListAtom,
  commissionsSortTermAtom,
  currentDateRangeAtom,
  selectedCommisionItemAtom,
} from '../../state';
import {
  Commission,
  CommissionPaginationArgs,
  PaymentStatus,
} from '../../typings';
import getCommissionsAPI from '../../request/get-commissions';
import useNotifications from '../../../../hooks/useNotifications';
import { getChannelDetails } from '../../../../utils/getSetChannelDetails';
import usePaginatedQuery, {
  UsePaginatedQueryResult,
} from '../../../../hooks/usePaginatedQuery';
import Loader from '../../../../components/loader';
import StatusBadge, { StatusBadgeTypes } from '../../../../components/badge';
import { ReactComponent as ReceiptBill } from '../../../../../icons/svgs/receipt_bill.svg';
import { ReactComponent as ReceiptBillCheck } from '../../../../../icons/svgs/receipt_bill_check.svg';
import { ReactComponent as ReceiptBillEdit } from '../../../../../icons/svgs/receipt_bill_edit.svg';
import { ReactComponent as Eye } from '../../../../../icons/svgs/eye.svg';
import { urlReplacer } from '../../../../utils/url-helpers';
import { ROUTES } from '../../../../routes/auth-routes/routes';
import { PAYOUT_TABLE_COLUMN_WIDTHS } from '../../constants';
import { formatCurrency } from '../../../../utils/currencyFormatter';
import { formatDateToMonthYear } from '../../utils';

const TableComponents = {
  Scroller: forwardRef((props, ref: ForwardedRef<HTMLDivElement>) => (
    <TableContainer {...props} ref={ref} />
  )),
  Table: (props: any) => (
    <Table {...props} style={{ borderCollapse: 'separate' }} />
  ),
  TableHead: TableHead as any,
  TableRow: TableRow as any,
  TableBody: forwardRef((props, ref: ForwardedRef<HTMLTableSectionElement>) => (
    <TableBody {...props} ref={ref} />
  )),
  TableFoot: forwardRef((props, ref: ForwardedRef<HTMLTableSectionElement>) => (
    <TableFooter
      {...props}
      ref={ref}
      style={{
        position: 'unset',
      }}
    />
  )),
};

const PayoutCommissionsListTable = () => {
  const sortValue = useAtomValue(commissionsSortTermAtom);
  const [currentDateRange, setCurrentDateRange] = useAtom(currentDateRangeAtom);
  const [paymentStatusFilter, setPaymentStatusFilter] = useAtom(
    commisionPaymentStatusFilterAtom
  );
  const setSelectedCommissionItem = useSetAtom(selectedCommisionItemAtom);
  const navigation = useNavigate();
  const { addNotification } = useNotifications();

  const getter = useCallback(
    async (paginationData?: CommissionPaginationArgs) => {
      const response = await getCommissionsAPI(
        {
          sort: {
            sortBy: sortValue.sortFilter,
            direction: sortValue.direction,
          },
          dateRange: {
            startDate: currentDateRange.startDate ?? '',
            endDate: currentDateRange.endDate ?? '',
          },
          paymentStatus: paymentStatusFilter,
        },
        paginationData
      );
      return {
        data: response.data,
        paginationData: {
          nextDate: response.nextRow.nextDate,
          nextCurrencyCode: response.nextRow.nextCurrencyCode,
        },
      } as UsePaginatedQueryResult<Commission, CommissionPaginationArgs>;
    },
    [
      sortValue.sortFilter,
      sortValue.direction,
      currentDateRange.startDate,
      currentDateRange.endDate,
      // paymentStatusFilter,
    ]
  );

  const {
    requestData: { loading, error, data: commissions, isFetchingMore },
    fetchMore: fetchMoreCommissions,
  } = usePaginatedQuery<Commission, CommissionPaginationArgs>({
    requestAtom: commissionsListAtom,
    getter: getter,
    cacheData: false,
  });

  const renderTableRows = () => {
    if (loading) {
      return (
        <TableBody>
          <TableRow>
            <TableCell colSpan={9}>
              <Box
                css={css`
                  height: 300px;
                  width: 100%;
                `}
                display={'flex'}
                flexDirection={'column'}
                justifyContent={'center'}
                alignItems={'center'}
                gap={'8px'}
              >
                <Loader />
              </Box>
            </TableCell>
          </TableRow>
        </TableBody>
      );
    }
    let emptyTableInfo = `No payout commissions found`;

    if (error) {
      emptyTableInfo = 'An error occurred. Please try again';
    }
    return (
      <TableBody>
        <TableRow>
          <TableCell colSpan={9}>
            <Box
              css={css`
                height: 300px;
                margin: auto;
              `}
              display={'flex'}
              flexDirection={'column'}
              justifyContent={'center'}
              alignItems={'center'}
              gap={'8px'}
            >
              <Typography
                css={css`
                  color: rgba(0, 0, 0, 0.87);
                  font-size: 14px;
                  font-weight: 400;
                `}
              >
                {emptyTableInfo}
              </Typography>
            </Box>
          </TableCell>
        </TableRow>
      </TableBody>
    );
  };

  const renderHeader = () => {
    return (
      <TableRow
        css={css`
          background-color: #fff;
        `}
      >
        <TableCell
          css={css`
            width: ${PAYOUT_TABLE_COLUMN_WIDTHS.PERIOD.width};
            min-width: ${PAYOUT_TABLE_COLUMN_WIDTHS.PERIOD.minWidth};
            max-width: ${PAYOUT_TABLE_COLUMN_WIDTHS.PERIOD.maxWidth};
          `}
        >
          <Typography
            css={css`
              font-size: 14px;
              font-weight: 500;
              color: '#2d2d2d';
            `}
          >
            Period
          </Typography>
        </TableCell>
        <TableCell
          css={css`
            width: ${PAYOUT_TABLE_COLUMN_WIDTHS.TOTAL_AMOUNT.width};
            min-width: ${PAYOUT_TABLE_COLUMN_WIDTHS.TOTAL_AMOUNT.minWidth};
            max-width: ${PAYOUT_TABLE_COLUMN_WIDTHS.TOTAL_AMOUNT.maxWidth};
          `}
        >
          <Typography
            css={css`
              font-size: 14px;
              font-weight: 500;
              color: '#2d2d2d';
            `}
          >
            Total Amount
          </Typography>
        </TableCell>
        {/* <TableCell
          css={css`
            width: ${PAYOUT_TABLE_COLUMN_WIDTHS.PAYMENT_STATUS.width};
            min-width: ${PAYOUT_TABLE_COLUMN_WIDTHS.PAYMENT_STATUS.minWidth};
            max-width: ${PAYOUT_TABLE_COLUMN_WIDTHS.PAYMENT_STATUS.maxWidth};
          `}
        >
          <Typography
            css={css`
              font-size: 14px;
              font-weight: 500;
              color: '#2d2d2d';
            `}
          >
            Payment Status
          </Typography>
        </TableCell> */}
        {/* <TableCell
          css={css`
            width: ${PAYOUT_TABLE_COLUMN_WIDTHS.UPLOAD_INVOICE.width};
            min-width: ${PAYOUT_TABLE_COLUMN_WIDTHS.UPLOAD_INVOICE.minWidth};
            max-width: ${PAYOUT_TABLE_COLUMN_WIDTHS.UPLOAD_INVOICE.maxWidth};
          `}
        >
          <Typography
            align="center"
            css={css`
              font-size: 14px;
              font-weight: 500;
              color: '#2d2d2d';
            `}
          >
            Upload Invoice
          </Typography>
        </TableCell> */}
        <TableCell
          css={css`
            width: ${PAYOUT_TABLE_COLUMN_WIDTHS.INVOICE_WORKING.width};
            min-width: ${PAYOUT_TABLE_COLUMN_WIDTHS.INVOICE_WORKING.minWidth};
            max-width: ${PAYOUT_TABLE_COLUMN_WIDTHS.INVOICE_WORKING.maxWidth};
          `}
        >
          <Typography
            align="center"
            css={css`
              font-size: 14px;
              font-weight: 500;
              color: '#2d2d2d';
            `}
          >
            Invoice Working
          </Typography>
        </TableCell>
      </TableRow>
    );
  };

  const getUploadInvoiceButtonText = (paymentStatus: PaymentStatus) => {
    switch (paymentStatus) {
      case PaymentStatus.PAID:
        return 'View details';
      case PaymentStatus.FAILED:
      case PaymentStatus.UNDER_REVIEW:
        return 'Re-upload invoice';
      case PaymentStatus.PROCESSING:
        return 'Upload invoice';
      default:
        return 'Upload invoice';
    }
  };

  const renderUploadInvoiceButtonIcon = (paymentStatus: PaymentStatus) => {
    switch (paymentStatus) {
      case PaymentStatus.PAID:
        return <ReceiptBillCheck />;
      case PaymentStatus.FAILED:
      case PaymentStatus.UNDER_REVIEW:
        return <ReceiptBillEdit />;
      case PaymentStatus.PROCESSING:
        return <ReceiptBill />;
      default:
        return <ReceiptBill />;
    }
  };

  const getDataRowMap = useCallback(
    (index: number, payoutCommission: Commission) => {
      return (
        <>
          <TableCell
            component="th"
            scope="row"
            css={css`
              width: ${PAYOUT_TABLE_COLUMN_WIDTHS.PERIOD.width};
              min-width: ${PAYOUT_TABLE_COLUMN_WIDTHS.PERIOD.minWidth};
              max-width: ${PAYOUT_TABLE_COLUMN_WIDTHS.PERIOD.maxWidth};
              padding-right: 0px;
            `}
          >
            <Typography
              css={css`
                overflow: auto;
                white-space: wrap;
                word-break: break-word;
                font-size: 14px;
                color: #2d2d2d;
                max-width: 200px;
              `}
            >
              {formatDateToMonthYear(payoutCommission.date)}
            </Typography>
          </TableCell>
          <TableCell
            css={css`
              width: ${PAYOUT_TABLE_COLUMN_WIDTHS.TOTAL_AMOUNT.width};
              min-width: ${PAYOUT_TABLE_COLUMN_WIDTHS.TOTAL_AMOUNT.minWidth};
              max-width: ${PAYOUT_TABLE_COLUMN_WIDTHS.TOTAL_AMOUNT.maxWidth};
            `}
          >
            {formatCurrency(payoutCommission.totalAmount, payoutCommission.currencyCode)}
          </TableCell>
          {/* <TableCell
            css={css`
              width: ${PAYOUT_TABLE_COLUMN_WIDTHS.PAYMENT_STATUS.width};
              min-width: ${PAYOUT_TABLE_COLUMN_WIDTHS.PAYMENT_STATUS.minWidth};
              max-width: ${PAYOUT_TABLE_COLUMN_WIDTHS.PAYMENT_STATUS.maxWidth};
            `}
          >
            <StatusBadge
              type={
                payoutCommission.paymentStatus as unknown as StatusBadgeTypes
              }
            />
          </TableCell> */}
          {/* <TableCell
            css={css`
              width: ${PAYOUT_TABLE_COLUMN_WIDTHS.UPLOAD_INVOICE.width};
              min-width: ${PAYOUT_TABLE_COLUMN_WIDTHS.UPLOAD_INVOICE.minWidth};
              max-width: ${PAYOUT_TABLE_COLUMN_WIDTHS.UPLOAD_INVOICE.maxWidth};
            `}
            align="center"
          >
            <Box
              css={css`
                width: 100%;
                display: flex;
                justify-content: center;
                align-items: center;
              `}
            >
              <Button
                variant="text"
                css={css`
                  display: flex;
                  height: 40px;
                  padding: 8px 16px 8px 12px;
                  align-items: center;
                  justify-content: center;
                  gap: 8px;
                `}
                onClick={() => {
                  setSelectedCommissionItem(payoutCommission);
                }}
              >
                {renderUploadInvoiceButtonIcon(payoutCommission.paymentStatus)}
                <Typography
                  css={css`
                    color: #3f906d;
                    font-family: Inter;
                    font-size: 14px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: normal;
                  `}
                >
                  {getUploadInvoiceButtonText(payoutCommission.paymentStatus)}
                </Typography>
              </Button>
            </Box>
          </TableCell> */}
          <TableCell
            css={css`
              width: ${PAYOUT_TABLE_COLUMN_WIDTHS.INVOICE_WORKING.width};
              min-width: ${PAYOUT_TABLE_COLUMN_WIDTHS.INVOICE_WORKING.minWidth};
              max-width: ${PAYOUT_TABLE_COLUMN_WIDTHS.INVOICE_WORKING.maxWidth};
            `}
            align="center"
          >
            <Box
              css={css`
                width: 100%;
                display: flex;
                justify-content: center;
                align-items: center;
              `}
              onClick={() => {
                navigation(urlReplacer(ROUTES.INVOICE_WORKING_PAGE, [[':payoutCommissionId', payoutCommission.channelPartnerPayoutInvoiceId]]));
              }}
            >
              <Button
                variant="text"
                css={css`
                  display: flex;
                  height: 40px;
                  padding: 8px 16px 8px 12px;
                  align-items: center;
                  justify-content: center;
                  gap: 8px;
                `}
              >
                <Eye />
                <Typography
                  css={css`
                    color: #3f906d;
                    font-family: Inter;
                    font-size: 14px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: normal;
                  `}
                >
                  View working
                </Typography>
              </Button>
            </Box>
          </TableCell>
        </>
      );
    },
    []
  );

  const renderLoaderInFooter = () => {
    if (isFetchingMore) {
      return (
        <TableRow>
          <TableCell colSpan={7}>
            <Box
              css={css`
                height: 50px;
                display: flex;
                justify-content: center;
                align-items: center;
              `}
            >
              <Loader />
            </Box>
          </TableCell>
        </TableRow>
      );
    }
    return null;
  };

  if (
    !loading &&
    !error &&
    Array.isArray(commissions) &&
    commissions.length > 0
  ) {
    return (
      <>
        <TableVirtuoso
          style={{
            height: `80vh`,
            width: '100%',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            backgroundColor: '#fff',
          }}
          css={css`
            &::-webkit-scrollbar {
              display: none; /* Hide scrollbar for WebKit browsers */
            }
            -ms-overflow-style: none; /* Hide scrollbar for IE and Edge */
            scrollbar-width: none; /* Hide scrollbar for Firefox */
          `}
          data={commissions}
          fixedHeaderContent={renderHeader}
          totalCount={(commissions ?? []).length}
          itemContent={getDataRowMap}
          endReached={fetchMoreCommissions}
          components={{
            ...TableComponents,
            EmptyPlaceholder: () => <>...</>,
          }}
          fixedFooterContent={renderLoaderInFooter}
        />
      </>
    );
  }

  return (
    <TableContainer
      component={Paper}
      css={css`
        border-radius: 12px;
      `}
    >
      <Table sx={{ minWidth: 650 }} aria-label="org table">
        <TableHead>{renderHeader()}</TableHead>
        {renderTableRows()}
      </Table>
    </TableContainer>
  );
};

export default PayoutCommissionsListTable;
